import { useEffect } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { Avatar } from '@nexply/ui';
import type { VCardContact } from '@nexply/core';
import { Building2, Mail, MapPin, Phone } from 'lucide-react';

export type SwipeDir = 'left' | 'right';

const THRESHOLD = 110;
const VELOCITY = 500;
const FLY_OUT = 700;

export interface ContactSwipeCardProps {
  contact: VCardContact;
  /** Von außen ausgelöster Flug (Buttons/Tastatur). null = Karte liegt normal. */
  fly: SwipeDir | null;
  onExit: (dir: SwipeDir) => void;
}

export function ContactSwipeCard({ contact, fly, onExit }: ContactSwipeCardProps) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-260, 0, 260], [-12, 0, 12]);
  const cardOpacity = useTransform(x, [-320, -180, 0, 180, 320], [0.2, 1, 1, 1, 0.2]);
  const yes = useTransform(x, [24, 130], [0, 1]);
  const no = useTransform(x, [-130, -24], [1, 0]);

  function leave(dir: SwipeDir) {
    animate(x, dir === 'right' ? FLY_OUT : -FLY_OUT, {
      type: 'spring',
      stiffness: 320,
      damping: 34,
      restDelta: 4,
      onComplete: () => onExit(dir),
    });
  }

  useEffect(() => {
    if (fly) leave(fly);
    // leave() hängt nur an stabilen Werten — bewusst nur auf fly reagieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fly]);

  return (
    <motion.div
      drag="x"
      dragElastic={0.7}
      dragMomentum={false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(_, info) => {
        const far = Math.abs(info.offset.x) > THRESHOLD;
        const fast = Math.abs(info.velocity.x) > VELOCITY;
        if (far || fast) {
          leave(info.offset.x > 0 || info.velocity.x > 0 ? 'right' : 'left');
        } else {
          animate(x, 0, { type: 'spring', stiffness: 400, damping: 32 });
        }
      }}
      style={{ x, rotate, opacity: cardOpacity, touchAction: 'none' }}
      className="absolute inset-0 cursor-grab active:cursor-grabbing select-none rounded-3xl border border-line-strong bg-surface-2 shadow-card-lg overflow-hidden"
    >
      {/* Farbige Hinweise beim Ziehen — Deckkraft folgt der Distanz. */}
      <motion.div
        style={{ opacity: yes }}
        className="pointer-events-none absolute inset-0 z-10 bg-success/10 border-2 border-success/50 rounded-3xl"
      >
        <span className="absolute left-5 top-5 -rotate-12 rounded-xl border-2 border-success px-3 py-1 text-lg font-bold uppercase tracking-wider text-success">
          Übernehmen
        </span>
      </motion.div>
      <motion.div
        style={{ opacity: no }}
        className="pointer-events-none absolute inset-0 z-10 bg-danger/10 border-2 border-danger/50 rounded-3xl"
      >
        <span className="absolute right-5 top-5 rotate-12 rounded-xl border-2 border-danger px-3 py-1 text-lg font-bold uppercase tracking-wider text-danger">
          Verwerfen
        </span>
      </motion.div>

      <div className="flex h-full flex-col items-center px-6 pb-6 pt-10 text-center">
        <Avatar name={contact.name} src={contact.photoUrl ?? null} size="xl" />
        <div className="mt-4 text-2xl font-semibold tracking-tight text-text">{contact.name}</div>
        {contact.role && <div className="mt-1 text-sm text-text-dim">{contact.role}</div>}

        <div className="mt-5 w-full space-y-2 text-left">
          {contact.company && <Line icon={<Building2 className="h-4 w-4" />} value={contact.company} />}
          {contact.email && <Line icon={<Mail className="h-4 w-4" />} value={contact.email} />}
          {contact.phone && <Line icon={<Phone className="h-4 w-4" />} value={contact.phone} />}
          {contact.city && (
            <Line
              icon={<MapPin className="h-4 w-4" />}
              value={[contact.city, contact.country].filter(Boolean).join(', ')}
            />
          )}
        </div>

        {!contact.company && !contact.email && !contact.role && (
          <div className="mt-4 text-xs text-text-muted">Nur Name und Telefonnummer bekannt.</div>
        )}
      </div>
    </motion.div>
  );
}

function Line({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl bg-raise px-3 py-2">
      <span className="shrink-0 text-gold">{icon}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-text-dim">{value}</span>
    </div>
  );
}
